"use client";

import React, { useState, useEffect, useCallback, startTransition, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ArrowLeft, CheckCircle, AlertCircle, Save, Loader2, Trash2, HelpCircle } from "lucide-react";
import { DocumentManager } from "../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { ConfirmationDialog } from "../ui/confirmation-dialog";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { ReviewComment } from "./ReviewComment";
import { MessageSquare } from "lucide-react";

export interface TailoringQuestion {
  id: string;
  question: string;
  type: string;
  options?: string[];
  subItems?: Array<{
    id: string;
    number?: string;
    text: string;
    type: string;
  }>;
  hasDocumentManager?: boolean; // Indicates this question should have a DocumentManager for subItems
  guidance?: {
    text: string;
    items: string[];
  };
  condition?: {
    questionId: string;
    value: string;
    sourceType?: "radio" | "checkbox";
  }; // Condition for showing this question based on another question's answer
  follow_up?: {
    condition: string;
    id: string;
    question: string;
    type: string;
    options?: string[];
    tableConfig?: {
      columns: Array<{
        key: string;
        label: string;
        type: string;
        options?: string[];
        readOnly?: boolean;
        sourceColumn?: string;
      }>;
      allowAdd?: boolean;
      allowDelete?: boolean;
      sourceQuestionId?: string;
    };
  };
  tableConfig?: {
    columns: Array<{
      key: string;
      label: string;
      type: string;
      options?: string[];
      readOnly?: boolean;
      sourceColumn?: string;
    }>;
    allowAdd?: boolean;
    allowDelete?: boolean;
    sourceQuestionId?: string;
  };
}

interface QuestionResponse {
  selectedOption?: string;
  textResponse?: {
    html: string;
    plainText: string;
  } | null;
  documents?: string[];
  table?: any[];
}

interface BaseTailoringQuestionsProps {
  areaName: string;
  title: string;
  description: string;
  questions: TailoringQuestion[];
  onBack?: () => void;
  onComplete?: (answers: Record<string, string>) => void;
}

export default function BaseTailoringQuestions({
  areaName,
  title,
  description,
  questions: initialQuestions,
  onBack,
  onComplete,
}: BaseTailoringQuestionsProps) {
  const { isArchivalMode } = useArchivalMode();
  // Use useMemo to ensure questions are stable and don't cause unnecessary re-renders
  const stableQuestions = useMemo(() => initialQuestions, []);
  const [questions, setQuestions] = useState<TailoringQuestion[]>(stableQuestions);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [checkboxAnswers, setCheckboxAnswers] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [questionResponses, setQuestionResponses] = useState<Record<string, QuestionResponse>>({});
  const [isEditMode, setIsEditMode] = useState<boolean>(true);
  const [sharePointSaveStatus, setSharePointSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  
  // Prevent edit mode in archival mode
  useEffect(() => {
    if (isArchivalMode && isEditMode) {
      setIsEditMode(false);
    }
  }, [isArchivalMode, isEditMode]);
  const [sharePointSaveError, setSharePointSaveError] = useState<string | null>(null);
  const [showStopEditingDialog, setShowStopEditingDialog] = useState<boolean>(false);
  const [expandedGuidance, setExpandedGuidance] = useState<Record<string, boolean>>({});
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("");
      }
    };
    loadCompanyName();
  }, []);

  // Load tailoring questions and responses from SharePoint
  useEffect(() => {
    const loadData = async () => {
      if (!companyName) {
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        setQuestions(stableQuestions);
        
        // Determine if this is a Concluding Procedures area
        const isConcludingProcedures = areaName === "AUDREP" || areaName === "CARO" || areaName === "CTCWG" || areaName === "SUBSEV" || areaName === "SE" || areaName === "WR" || areaName === "LIT" || areaName === "CLR" || areaName === "REMB" || areaName === "ITAUD" || areaName === "GC" || areaName === "EARM";
        
        // Determine the file name based on phase
        const fileName = isConcludingProcedures 
          ? 'ConcludingProcedures_TailoringQuestions_Responses.json'
          : 'Execution_TailoringQuestions_Responses.json';
        
        // For Concluding Procedures, we need to get the workspace name
        let targetAreaName = areaName;
        if (isConcludingProcedures) {
          const workspaceNameMap: Record<string, string> = {
            'CARO': 'CARO',
            'AUDREP': 'Audit Report & Opinion',
            'CTCWG': 'Communication with those charged with governance',
            'SUBSEV': 'Subsequent Events',
            'SE': 'Subsequent Events',
            'WR': 'Obtain Written Representations',
            'LIT': 'Litigations & Claims',
            'CLR': 'Compliance with laws and regulations',
            'REMB': 'Review of estimates for management bias',
            'ITAUD': 'Internal Audit',
            'GC': 'Going Concern',
            'EARM': 'Evaluate Audit Results Including Misstatements'
          };
          targetAreaName = workspaceNameMap[areaName] || areaName;
        }
        
        // Load responses directly from SharePoint file using downloadClientJson
        // This works in both normal and archival mode (read operation)
        if (window.sharePointAPI?.downloadClientJson) {
          console.log(`[${areaName}] Loading responses from SharePoint file: ${fileName} (archival mode: ${isArchivalMode})...`);
          const result = await window.sharePointAPI.downloadClientJson(fileName);

          console.log(`[${areaName}] Load result:`, {
            success: result?.success,
            hasData: !!result?.data,
            dataKeys: result?.data ? Object.keys(result.data) : [],
            archivalMode: isArchivalMode,
            fileName,
            targetAreaName
          });

          if (result.success && result.data) {
            // Extract data for the target area
            const areaData = result.data[targetAreaName];
            
            if (areaData) {
              const loadedResponses: Record<string, QuestionResponse> = {};
              const loadedAnswers: Record<string, string> = {};
              
              for (const question of stableQuestions) {
                const questionData = areaData[question.id];
                console.log(`[${areaName}] Processing question ${question.id}:`, questionData);
                
                if (questionData) {
                  loadedResponses[question.id] = {
                    selectedOption: questionData.selectedOption || "",
                    textResponse: questionData.textResponse || null,
                    documents: Array.isArray(questionData.documents) ? questionData.documents : (questionData.documents ? [questionData.documents] : []),
                    table: Array.isArray(questionData.table) ? questionData.table : (questionData.table ? [questionData.table] : [])
                  };
                  
                  // Load selectedOption to answers state for display in RadioGroup/Textarea
                  if (question.type === "checkbox" && questionData.selectedOption) {
                    // For checkbox, parse comma-separated string into array
                    const checkboxOptions = String(questionData.selectedOption).split(",").map(s => s.trim()).filter(s => s);
                    setCheckboxAnswers(prev => ({ ...prev, [question.id]: checkboxOptions }));
                    console.log(`[${areaName}]  Loaded checkbox answers for ${question.id}:`, checkboxOptions);
                  } else if (questionData.selectedOption) {
                    loadedAnswers[question.id] = String(questionData.selectedOption);
                    console.log(`[${areaName}]  Loaded answer for ${question.id}: "${questionData.selectedOption}"`);
                  } else if (question.type === "text" && questionData.textResponse?.plainText) {
                    // For text questions, use plainText if available
                    loadedAnswers[question.id] = questionData.textResponse.plainText;
                    console.log(`[${areaName}]  Loaded text answer for ${question.id}`);
                  }
                }
                
                // Also check follow-up questions
                if (question.follow_up) {
                  const followUpData = areaData[question.follow_up.id];
                  if (followUpData) {
                    loadedResponses[question.follow_up.id] = {
                      selectedOption: followUpData.selectedOption || "",
                      textResponse: followUpData.textResponse || null,
                      documents: Array.isArray(followUpData.documents) ? followUpData.documents : (followUpData.documents ? [followUpData.documents] : []),
                      table: Array.isArray(followUpData.table) ? followUpData.table : (followUpData.table ? [followUpData.table] : [])
                    };
                    if (followUpData.selectedOption) {
                      loadedAnswers[question.follow_up.id] = String(followUpData.selectedOption);
                      console.log(`[${areaName}]  Loaded answer for follow-up ${question.follow_up.id}: "${followUpData.selectedOption}"`);
                    }
                  }
                }
              }
              
              console.log(`[${areaName}]  Final loadedAnswers:`, JSON.stringify(loadedAnswers, null, 2));
              console.log(`[${areaName}]  Final loadedResponses keys:`, Object.keys(loadedResponses));
              
              // Set both states
              setQuestionResponses(loadedResponses);
              setAnswers(loadedAnswers);
              
              console.log(`[${areaName}]  Successfully loaded and set responses for ${Object.keys(loadedAnswers).length} questions`);
            } else {
              console.log(`[${areaName}]  No data found for area "${targetAreaName}" in ${fileName}`);
              setQuestionResponses({});
              setAnswers({});
            }
          } else {
            console.log(`[${areaName}]  No responses found in SharePoint file ${fileName}`);
            setQuestionResponses({});
            setAnswers({});
          }
        } else {
          console.warn(`[${areaName}] window.sharePointAPI.downloadClientJson is not available`);
          setQuestionResponses({});
          setAnswers({});
        }
      } catch (error) {
        console.error(`[${areaName}] Failed to load responses from SharePoint:`, error);
        setError(error instanceof Error ? error.message : 'Failed to load responses from SharePoint');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [companyName, areaName, isArchivalMode]); // Added isArchivalMode to dependencies

  // Debug: Log when answers change to verify state updates
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      console.log(`[${areaName}] Answers state updated:`, answers);
      console.log(`[${areaName}] Current answers values:`, JSON.stringify(answers, null, 2));
    }
  }, [answers, areaName]);

  // Save responses to SharePoint using tailoring questions API
  const saveResponses = useCallback(async () => {
    if (!companyName) return;
    
    try {
      // Build responses object in the format expected by the API
      const responsesToSave: Record<string, any> = {};
      
      // Load existing responses first to preserve data for questions we're not updating
      let existingResponses: Record<string, any> = {};
      if (window.tailoringQuestionsAPI?.loadResponses) {
        try {
          const loadResult = await window.tailoringQuestionsAPI.loadResponses({
            companyName,
            areaName
          });
          
          if (loadResult.success && loadResult.data) {
            existingResponses = loadResult.data;
            console.log(`[${areaName}] Loaded existing responses for merge:`, Object.keys(existingResponses));
          }
        } catch (loadError) {
          console.warn(`[${areaName}] Could not load existing responses, will create new:`, loadError);
        }
      }
      
      // Build responses for all questions we have data for
      for (const question of questions) {
        const questionId = question.id;
        const currentResponse = questionResponses[questionId] || {};
        const answer = answers[questionId] || "";
        
        // Check if we have any data to save
        // For textResponse, we need to check if it exists in currentResponse (even if null/empty)
        const hasTextResponse = 'textResponse' in currentResponse;
        const hasCheckboxData = question.type === "checkbox" && checkboxAnswers[questionId] && checkboxAnswers[questionId].length > 0;
        const hasNewData = answer || currentResponse.selectedOption || hasTextResponse || currentResponse.documents || currentResponse.table || hasCheckboxData;
        const hasExistingData = existingResponses[questionId] !== undefined;
        
        if (hasNewData || hasExistingData) {
          // Non-destructive merge: preserve existing fields, update with new data
          // For textResponse, always include it if it exists in currentResponse (even if null)
          const textResponseValue = hasTextResponse 
            ? currentResponse.textResponse 
            : (existingResponses[questionId]?.textResponse || null);
          
          // Handle checkbox type - save as comma-separated string
          let selectedOptionValue = answer || currentResponse.selectedOption || existingResponses[questionId]?.selectedOption || "";
          if (question.type === "checkbox") {
            const checkboxSelections = checkboxAnswers[questionId] || [];
            selectedOptionValue = checkboxSelections.join(", ");
          }
          
          responsesToSave[questionId] = {
            ...(existingResponses[questionId] || {}), // Preserve existing fields
            selectedOption: selectedOptionValue,
            textResponse: textResponseValue,
            documents: currentResponse.documents !== undefined ? currentResponse.documents : (existingResponses[questionId]?.documents || []),
            table: currentResponse.table !== undefined ? currentResponse.table : (existingResponses[questionId]?.table || [])
          };
          
          // Log textResponse for debugging
          if (hasTextResponse) {
            console.log(`[${areaName}] Saving textResponse for ${questionId}:`, {
              hasHtml: !!textResponseValue?.html,
              hasPlainText: !!textResponseValue?.plainText,
              htmlLength: textResponseValue?.html?.length || 0,
              plainTextLength: textResponseValue?.plainText?.length || 0
            });
          }
        }
        
        // Also handle follow-up questions
        if (question.follow_up) {
          const followUpId = question.follow_up.id;
          const followUpResponse = questionResponses[followUpId] || {};
          const followUpAnswer = answers[followUpId] || "";
          
          // Check if we have any data to save for follow-up
          const hasFollowUpTextResponse = 'textResponse' in followUpResponse;
          const hasNewFollowUpData = followUpAnswer || followUpResponse.selectedOption || hasFollowUpTextResponse || followUpResponse.documents || followUpResponse.table;
          const hasExistingFollowUpData = existingResponses[followUpId] !== undefined;
          // Always include follow-ups with "always" condition
          const isAlwaysCondition = question.follow_up.condition === "always";
          
          if (hasNewFollowUpData || hasExistingFollowUpData || isAlwaysCondition) {
            // For textResponse, always include it if it exists in followUpResponse (even if null)
            const followUpTextResponseValue = hasFollowUpTextResponse 
              ? followUpResponse.textResponse 
              : (existingResponses[followUpId]?.textResponse || null);
            
            responsesToSave[followUpId] = {
              ...(existingResponses[followUpId] || {}), // Preserve existing fields
              selectedOption: followUpAnswer || followUpResponse.selectedOption || existingResponses[followUpId]?.selectedOption || "",
              textResponse: followUpTextResponseValue,
              documents: followUpResponse.documents !== undefined ? followUpResponse.documents : (existingResponses[followUpId]?.documents || []),
              table: followUpResponse.table !== undefined ? followUpResponse.table : (existingResponses[followUpId]?.table || [])
            };
            
            // Log textResponse for debugging
            if (hasFollowUpTextResponse) {
              console.log(`[${areaName}] Saving textResponse for follow-up ${followUpId}:`, {
                hasHtml: !!followUpTextResponseValue?.html,
                hasPlainText: !!followUpTextResponseValue?.plainText,
                htmlLength: followUpTextResponseValue?.html?.length || 0,
                plainTextLength: followUpTextResponseValue?.plainText?.length || 0
              });
            }
          }
        }
      }
      
      // Log changes before saving
      const changedQuestions = Object.keys(responsesToSave).filter(qId => {
        const existing = existingResponses[qId];
        const newData = responsesToSave[qId];
        return !existing || JSON.stringify(existing) !== JSON.stringify(newData);
      });
      
      if (changedQuestions.length > 0) {
        console.log(`[${areaName}] Changes detected for questions:`, changedQuestions);
        console.log(`[${areaName}] Saving ${Object.keys(responsesToSave).length} question responses`);
      } else {
        console.log(`[${areaName}] No changes detected - responses are up to date`);
      }
      
      // Save using tailoring questions API
      if (window.tailoringQuestionsAPI?.saveResponses) {
        const result = await window.tailoringQuestionsAPI.saveResponses({
          companyName,
          areaName,
          responses: responsesToSave
        });
        
        if (!result.success) {
          throw new Error(result.error || "Failed to save");
        }
        
        console.log(`[${areaName}] Successfully saved responses to SharePoint`);
      } else {
        throw new Error("Tailoring Questions API not available");
      }
    } catch (error) {
      console.error(`[${areaName}] Failed to save responses to SharePoint:`, error);
      throw error;
    }
  }, [companyName, questions, answers, checkboxAnswers, questionResponses, areaName]);

  const handleAnswerChange = (questionId: string, answer: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
    
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        selectedOption: answer
      }
    }));
  };

  const handleCheckboxChange = (questionId: string, option: string, checked: boolean) => {
    setCheckboxAnswers(prev => {
      const current = prev[questionId] || [];
      const updated = checked 
        ? [...current, option].filter((v, i, arr) => arr.indexOf(v) === i) // Add and remove duplicates
        : current.filter(item => item !== option); // Remove if unchecked
      
      // Update questionResponses with comma-separated string
      setQuestionResponses(prevResp => ({
        ...prevResp,
        [questionId]: {
          ...prevResp[questionId],
          selectedOption: updated.join(", ")
        }
      }));
      
      return {
        ...prev,
        [questionId]: updated
      };
    });
  };
  
  const handleDocumentsChange = (questionId: string, documents: any[]) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        documents: documents.map(doc => doc.name || doc)
      }
    }));
  };
  
  const handleTextResponseChange = (questionId: string, textResponse: any) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        textResponse
      }
    }));
  };

  const handleTableChange = (questionId: string, tableData: any[]) => {
    setQuestionResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        table: tableData
      }
    }));
  };

  const handleComplete = async () => {
    if (isEditMode) {
      setSharePointSaveStatus('saving');
      try {
        // Trigger auto-save for all DocumentManager textboxes before saving
        // This ensures any unsaved textbox content is captured
        try {
          for (const question of questions) {
            if (question.type === "text" || question.type === "document") {
              const autoSaveMethod = (window as any)[`autoSaveTextbox_${question.id}`];
              if (autoSaveMethod && typeof autoSaveMethod === 'function') {
                autoSaveMethod();
              }
              // Also check follow-up questions
              if (question.follow_up && (question.follow_up.type === "text" || question.follow_up.type === "document")) {
                const followUpAutoSave = (window as any)[`autoSaveTextbox_${question.follow_up.id}`];
                if (followUpAutoSave && typeof followUpAutoSave === 'function') {
                  followUpAutoSave();
                }
              }
            }
          }
          // Small delay to ensure state updates complete
          await new Promise(resolve => setTimeout(resolve, 300));
        } catch (error) {
          console.warn(`[${areaName}] Failed to trigger auto-save for DocumentManager:`, error);
        }
        
        await saveResponses();
        setSharePointSaveStatus('saved');
        setIsEditMode(false);
      } catch (error) {
        setSharePointSaveStatus('error');
        setSharePointSaveError(error instanceof Error ? error.message : 'Save failed');
      }
    }
    
    if (onComplete) {
      onComplete(answers);
    }
  };

  const isQuestionAnswered = (questionId: string) => {
    const question = questions.find(q => q.id === questionId);
    if (question?.type === "checkbox") {
      const checkboxSelections = checkboxAnswers[questionId] || [];
      return checkboxSelections.length > 0;
    }
    
    const hasOptionAnswer =
      answers[questionId] !== undefined && answers[questionId] !== "";

    const response = questionResponses[questionId];
    const hasTextResponse =
      !!response?.textResponse &&
      (
        (response.textResponse.html && response.textResponse.html.trim() !== "") ||
        (response.textResponse.plainText && response.textResponse.plainText.trim() !== "")
      );
    const hasDocuments =
      !!response?.documents && Array.isArray(response.documents) && response.documents.length > 0;
    const hasTable =
      !!response?.table && Array.isArray(response.table) && response.table.length > 0;

    return hasOptionAnswer || hasTextResponse || hasDocuments || hasTable;
  };

  const isSubItemAnswered = (subItemId: string) => {
    const response = questionResponses[subItemId];
    return response && (
      (response.textResponse && response.textResponse.plainText.trim() !== "") ||
      (response.documents && response.documents.length > 0)
    );
  };

  const isQuestionWithSubItemsAnswered = (question: TailoringQuestion) => {
    if (!question.subItems || question.subItems.length === 0) return false;
    // For questions with subItems, check if the main question's DocumentManager has content
    if (question.hasDocumentManager) {
      const response = questionResponses[question.id];
      return response && (
        (response.textResponse && response.textResponse.plainText.trim() !== "") ||
        (response.documents && response.documents.length > 0)
      );
    }
    return false;
  };

  const shouldShowFollowUp = (question: TailoringQuestion) => {
    if (!question.follow_up) return false;
    // Handle "always" condition - always show the follow-up
    if (question.follow_up.condition === "always") return true;
    return answers[question.id] === question.follow_up.condition;
  };

  const shouldShowQuestion = (question: TailoringQuestion): boolean => {
    if (!question.condition) return true;
    
    // Check if the condition's prerequisite question exists and should be shown
    const prerequisiteQuestion = questions.find(q => q.id === question.condition!.questionId);
    if (prerequisiteQuestion) {
      // Recursively check if the prerequisite question should be shown
      // This ensures that if a parent question is hidden, child questions are also hidden
      if (!shouldShowQuestion(prerequisiteQuestion)) {
        return false;
      }
    }
    
    // Check if the condition's value matches
    if (question.condition.sourceType === "checkbox") {
      const selected = checkboxAnswers[question.condition.questionId] || [];
      return selected.includes(question.condition.value);
    }

    return answers[question.condition.questionId] === question.condition.value;
  };

  const allQuestionsAnswered = () => {
    return questions.every(q => {
      // For questions with subItems, check if the main question's DocumentManager is answered
      if (q.subItems && q.subItems.length > 0) {
        const mainAnswered = isQuestionWithSubItemsAnswered(q);
        const followUpAnswered = !shouldShowFollowUp(q) || 
                                  (q.follow_up?.condition === "always") || 
                                  isQuestionAnswered(q.follow_up!.id);
        return mainAnswered && followUpAnswered;
      }
      
      const mainAnswered = isQuestionAnswered(q.id);
      // For "always" condition follow-ups, they should always be considered (but not required to be answered)
      // For other follow-ups, check if they should show and if answered
      const followUpAnswered = !shouldShowFollowUp(q) || 
                                (q.follow_up?.condition === "always") || 
                                isQuestionAnswered(q.follow_up!.id);
      return mainAnswered && followUpAnswered;
    });
  };

  const renderQuestion = (question: TailoringQuestion, isFollowUp = false) => {
    // For questions with subItems, check if the main question's DocumentManager is answered
    const isAnswered = question.subItems && question.subItems.length > 0
      ? isQuestionWithSubItemsAnswered(question)
      : isQuestionAnswered(question.id);
    
    return (
      <Card key={question.id} className={`border-white/10 bg-white/5 text-white ${isFollowUp ? 'ml-6 border-l-2 border-l-[#4da3ff]/30' : ''}`}>
        <CardContent className="p-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                {isAnswered ? (
                  <CheckCircle className="h-5 w-5 text-green-400" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-white/40" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-start gap-2">
                  <Label className="text-sm font-medium text-white whitespace-pre-line flex-1">
                    {question.question}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setReviewCommentQuestionId(question.id)}
                      className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                      title="Review Comment"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </Button>
                    {question.guidance && (
                      <button
                        type="button"
                        onClick={() => setExpandedGuidance(prev => ({
                          ...prev,
                          [question.id]: !prev[question.id]
                        }))}
                        className="flex-shrink-0 p-1 text-white/60 hover:text-white hover:bg-white/10 rounded transition-colors"
                        aria-label="Toggle guidance"
                      >
                        <HelpCircle className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Render guidance if expanded */}
            {question.guidance && expandedGuidance[question.id] && (
              <div className="ml-8 mt-3 p-4 bg-white/5 border border-white/10 rounded-md">
                <div className="text-sm text-white/90 whitespace-pre-line">
                  <div className="mb-2">{question.guidance.text}</div>
                  <ul className="list-decimal list-inside space-y-1 ml-2">
                    {question.guidance.items.map((item, idx) => (
                      <li key={idx} className="text-white/80">{item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Render subItems if they exist - show as text labels only */}
            {question.subItems && question.subItems.length > 0 && (
              <div className="ml-8 mt-4 space-y-2">
                {question.subItems.map((subItem) => (
                  <div key={subItem.id} className="flex items-start gap-2">
                    <div className="flex-1">
                      <Label className="text-sm font-medium text-white/90">
                        {subItem.number && `${subItem.number}. `}{subItem.text}
                      </Label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Single DocumentManager for questions with subItems */}
            {question.subItems && question.subItems.length > 0 && question.hasDocumentManager && (
              <div className="ml-8 mt-4">
                <DocumentManager
                  sectionKey={areaName === "AUDREP" || areaName === "CARO" || areaName === "CTCWG" || areaName === "SUBSEV" || areaName === "SE" || areaName === "WR" || areaName === "LIT" || areaName === "CLR" || areaName === "REMB" || areaName === "ITAUD" || areaName === "GC" || areaName === "EARM" ? `concluding-${areaName.toLowerCase()}` : `execution-tailoring:${areaName}`}
                  itemId={question.id}
                  itemQuestion={question.question}
                  itemTextResponse={questionResponses[question.id]?.textResponse}
                  itemDocuments={questionResponses[question.id]?.documents?.map((doc: string | any) => {
                    if (typeof doc === 'string') {
                      return {
                        id: `doc_${doc}`,
                        type: 'linked' as const,
                        name: doc
                      };
                    }
                    return doc;
                  }) || []}
                  onDocumentsChange={(docs) => handleDocumentsChange(question.id, docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange(question.id, textResp)}
                  disabled={!isEditMode || isArchivalMode}
                />
              </div>
            )}

            {question.type === "checkbox" && question.options && !question.subItems && (
              <div className="ml-8 space-y-2">
                {question.options.map((option) => {
                  const isChecked = (checkboxAnswers[question.id] || []).includes(option);
                  return (
                    <div key={option} className="flex items-center space-x-2">
                      <Checkbox
                        id={`${question.id}-${option}`}
                        checked={isChecked}
                        onCheckedChange={(checked) => handleCheckboxChange(question.id, option, checked === true)}
                        disabled={!isEditMode || isArchivalMode}
                        className="border-white/20 data-[state=checked]:bg-[#4da3ff] data-[state=checked]:border-[#4da3ff]"
                      />
                      <Label
                        htmlFor={`${question.id}-${option}`}
                        className="text-sm text-white/80 cursor-pointer whitespace-pre-line flex-1"
                      >
                        {option}
                      </Label>
                    </div>
                  );
                })}
              </div>
            )}

            {question.type === "radio" && question.options && !question.subItems && (
              <>
                <RadioGroup
                  value={answers[question.id] || ""}
                  onValueChange={(value) => handleAnswerChange(question.id, value)}
                  disabled={!isEditMode || isArchivalMode}
                  className="ml-8"
                >
                  {question.options.map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                      <RadioGroupItem 
                        value={option} 
                        id={`${question.id}-${option}`}
                        className="border-white/20 text-[#4da3ff] focus:ring-[#4da3ff]/20"
                      />
                      <Label 
                        htmlFor={`${question.id}-${option}`}
                        className="text-sm text-white/80 cursor-pointer whitespace-pre-line"
                      >
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
                {/* Show DocumentManager inline for radio questions with follow-up */}
                {question.follow_up && (question.follow_up.condition === "always" || shouldShowFollowUp(question)) && (question.follow_up.type === "text" || question.follow_up.type === "document") && (
                  <div className="ml-8 mt-3">
                    <DocumentManager
                      sectionKey={areaName === "AUDREP" || areaName === "CARO" || areaName === "CTCWG" || areaName === "SUBSEV" || areaName === "SE" || areaName === "WR" || areaName === "LIT" || areaName === "CLR" || areaName === "REMB" || areaName === "ITAUD" || areaName === "GC" || areaName === "EARM" ? `concluding-${areaName.toLowerCase()}` : `execution-tailoring:${areaName}`}
                      itemId={question.follow_up.id}
                      itemQuestion={question.follow_up.question}
                      itemTextResponse={questionResponses[question.follow_up.id]?.textResponse}
                      itemDocuments={questionResponses[question.follow_up.id]?.documents?.map((doc: string | any) => {
                        // Handle both string and object formats
                        if (typeof doc === 'string') {
                          return {
                            id: `doc_${doc}`,
                            type: 'linked' as const,
                            name: doc
                          };
                        }
                        return doc;
                      }) || []}
                      onDocumentsChange={(docs) => handleDocumentsChange(question.follow_up!.id, docs)}
                      onTextResponseChange={(textResp) => handleTextResponseChange(question.follow_up!.id, textResp)}
                      disabled={!isEditMode}
                    />
                  </div>
                )}
                {/* Show table follow-up for radio questions */}
                {question.follow_up && shouldShowFollowUp(question) && question.follow_up.type === "table" && question.follow_up.tableConfig && (
                  <div className="ml-8 mt-3">
                    {question.follow_up.question && question.follow_up.question.trim() !== "" && (
                      <div className="mb-2 text-sm text-white/80">
                        {question.follow_up.question}
                      </div>
                    )}
                    <EventsTable
                      questionId={question.follow_up.id}
                      columns={question.follow_up.tableConfig.columns}
                      value={questionResponses[question.follow_up.id]?.table || []}
                      onChange={(tableData) => handleTableChange(question.follow_up!.id, tableData)}
                      allowAdd={question.follow_up.tableConfig.allowAdd !== false}
                      allowDelete={question.follow_up.tableConfig.allowDelete !== false}
                      disabled={!isEditMode || isArchivalMode}
                      sourceQuestionId={question.follow_up.tableConfig.sourceQuestionId}
                      sourceTableData={question.follow_up.tableConfig.sourceQuestionId ? (questionResponses[question.follow_up.tableConfig.sourceQuestionId]?.table || []) : undefined}
                    />
                  </div>
                )}
              </>
            )}

            {/* Table type */}
            {question.type === "table" && question.tableConfig && !question.subItems && (
              <div className="ml-8 mt-3">
                <EventsTable
                  questionId={question.id}
                  columns={question.tableConfig.columns}
                  value={questionResponses[question.id]?.table || []}
                  onChange={(tableData) => handleTableChange(question.id, tableData)}
                  allowAdd={question.tableConfig.allowAdd !== false}
                  allowDelete={question.tableConfig.allowDelete !== false}
                  disabled={!isEditMode || isArchivalMode}
                  sourceQuestionId={question.tableConfig.sourceQuestionId}
                  sourceTableData={question.tableConfig.sourceQuestionId ? (questionResponses[question.tableConfig.sourceQuestionId]?.table || []) : undefined}
                />
              </div>
            )}

            {/* DocumentManager handles both text and document types, including textbox */}
            {(question.type === "document" || question.type === "text") && !question.subItems && (
              <div className="ml-8 mt-3">
                <DocumentManager
                  sectionKey={areaName === "AUDREP" || areaName === "CARO" || areaName === "CTCWG" || areaName === "SUBSEV" || areaName === "SE" || areaName === "WR" || areaName === "LIT" || areaName === "CLR" || areaName === "REMB" || areaName === "ITAUD" || areaName === "GC" || areaName === "EARM" ? `concluding-${areaName.toLowerCase()}` : `execution-tailoring:${areaName}`}
                  itemId={question.id}
                  itemQuestion={question.question}
                  itemTextResponse={questionResponses[question.id]?.textResponse}
                  itemDocuments={questionResponses[question.id]?.documents?.map((doc: string | any) => {
                    // Handle both string and object formats
                    if (typeof doc === 'string') {
                      return {
                        id: `doc_${doc}`,
                        type: 'linked' as const,
                        name: doc
                      };
                    }
                    return doc;
                  }) || []}
                  onDocumentsChange={(docs) => handleDocumentsChange(question.id, docs)}
                  onTextResponseChange={(textResp) => handleTextResponseChange(question.id, textResp)}
                  disabled={!isEditMode || isArchivalMode}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          <div>
            <h2 className="text-xl font-semibold text-white">{title}</h2>
            <p className="text-sm text-white/60">Loading questions...</p>
          </div>
        </div>
        
        <Card className="border-white/10 bg-white/5 text-white">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4da3ff] mx-auto mb-4"></div>
              <p>Loading tailoring questions...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          <div>
            <h2 className="text-xl font-semibold text-white">{title}</h2>
            <p className="text-sm text-white/60">Error loading questions</p>
          </div>
        </div>
        
        <Card className="border-red-500/20 bg-red-500/5 text-white">
          <CardContent className="p-6">
            <div className="text-center text-red-300">
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {onBack && (
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          <div>
            <h2 className="text-xl font-semibold text-white">{title}</h2>
            <p className="text-sm text-white/60">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isEditMode ? (
            <Button
              onClick={() => {
                startTransition(() => {
                  setIsEditMode(true);
                });
              }}
              variant="outline"
              size="sm"
              className="border-white/20 text-white hover:bg-white/10"
              disabled={isArchivalMode}
            >
              Edit
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                onClick={async () => {
                  setSharePointSaveStatus('saving');
                  try {
                    // Trigger auto-save for all DocumentManager textboxes before saving
                    try {
                      for (const question of questions) {
                        if (question.type === "text" || question.type === "document") {
                          const autoSaveMethod = (window as any)[`autoSaveTextbox_${question.id}`];
                          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
                            autoSaveMethod();
                          }
                          // Also check follow-up questions
                          if (question.follow_up && (question.follow_up.type === "text" || question.follow_up.type === "document")) {
                            const followUpAutoSave = (window as any)[`autoSaveTextbox_${question.follow_up.id}`];
                            if (followUpAutoSave && typeof followUpAutoSave === 'function') {
                              followUpAutoSave();
                            }
                          }
                        }
                      }
                      // Small delay to ensure state updates complete
                      await new Promise(resolve => setTimeout(resolve, 300));
                    } catch (error) {
                      console.warn(`[${areaName}] Failed to trigger auto-save for DocumentManager:`, error);
                    }
                    
                    await saveResponses();
                    setSharePointSaveStatus('saved');
                    setIsEditMode(false);
                    setTimeout(() => setSharePointSaveStatus('idle'), 3000);
                  } catch (error) {
                    setSharePointSaveStatus('error');
                    setSharePointSaveError(error instanceof Error ? error.message : 'Save failed');
                  }
                }}
                disabled={sharePointSaveStatus === 'saving' || isArchivalMode}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {sharePointSaveStatus === 'saving' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </>
                )}
              </Button>
              <Button
                onClick={() => setShowStopEditingDialog(true)}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10"
                disabled={isArchivalMode}
              >
                Stop Editing
              </Button>
            </div>
          )}
        </div>
      </div>
      
      {/* Save Status Messages */}
      {sharePointSaveStatus === 'saved' && (
        <div className="rounded-md bg-green-500/10 border border-green-500/20 p-3 text-sm text-green-300">
          Changes saved successfully
        </div>
      )}
      {sharePointSaveStatus === 'error' && sharePointSaveError && (
        <div className="rounded-md bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-300">
          Error saving changes: {sharePointSaveError}
        </div>
      )}

      {/* Progress Indicator */}
      {questions.length > 0 && (
      <Card className="border-white/10 bg-white/5 text-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white">Progress</span>
              <Badge variant="outline" className="text-xs">
                {questions.filter(q =>
                  q.subItems && q.subItems.length > 0
                    ? isQuestionWithSubItemsAnswered(q)
                    : isQuestionAnswered(q.id)
                ).length} / {questions.length} answered
              </Badge>
            </div>
            <div className="text-xs text-white/60">
              {Math.round((
                questions.filter(q =>
                  q.subItems && q.subItems.length > 0
                    ? isQuestionWithSubItemsAnswered(q)
                    : isQuestionAnswered(q.id)
                ).length / questions.length
              ) * 100)}% complete
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Questions */}
      <div className="space-y-4">
        {questions.map((question) => {
          // Check if question should be shown based on condition
          if (!shouldShowQuestion(question)) {
            return null;
          }
          return (
            <div key={question.id} className="space-y-3">
              {renderQuestion(question)}
              {/* Only show follow-up as separate question if it's not an "always" condition, not a table (tables are shown inline), and not document/text (Add is shown inline in main question) */}
              {shouldShowFollowUp(question) && question.follow_up && question.follow_up.condition !== "always" && question.follow_up.type !== "table" && question.follow_up.type !== "document" && question.follow_up.type !== "text" && (
                <div className="transition-all duration-300">
                  {renderQuestion(question.follow_up as unknown as TailoringQuestion, true)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Actions */}
      <Card className="border-white/10 bg-white/5 text-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-white/60">
              {allQuestionsAnswered() 
                ? "All questions completed. You can proceed to the next step."
                : "Please answer all questions to proceed."
              }
            </div>
            <Button
              onClick={handleComplete}
              disabled={!allQuestionsAnswered() || isArchivalMode}
              className="bg-[#4da3ff] text-white hover:bg-[#4da3ff]/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Complete Assessment
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Stop Editing Confirmation Dialog */}
      <ConfirmationDialog
        open={showStopEditingDialog}
        onOpenChange={setShowStopEditingDialog}
        title="Stop Editing"
        description="Do you want to save your changes before stopping editing?"
        onYes={async () => {
          setShowStopEditingDialog(false);
          setSharePointSaveStatus('saving');
          try {
            await saveResponses();
            setSharePointSaveStatus('saved');
            setIsEditMode(false);
            setTimeout(() => setSharePointSaveStatus('idle'), 3000);
          } catch (error) {
            setSharePointSaveStatus('error');
            setSharePointSaveError(error instanceof Error ? error.message : 'Save failed');
            alert("Failed to save changes. Please try again.");
          }
        }}
        onNo={() => {
          setShowStopEditingDialog(false);
          setIsEditMode(false);
        }}
        onCancel={() => {
          setShowStopEditingDialog(false);
        }}
        yesText="Yes"
        noText="No"
        cancelText="Cancel"
      />
      
      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={questions.find(q => q.id === reviewCommentQuestionId)?.question}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
          workspace={areaName}
          subPhase={title}
        />
      )}
    </div>
  );
}

// Events Table Component
interface EventsTableProps {
  questionId: string;
  columns: Array<{
    key: string;
    label: string;
    type: string;
    options?: string[];
    readOnly?: boolean;
    sourceColumn?: string;
  }>;
  value: any[];
  onChange: (tableData: any[]) => void;
  allowAdd?: boolean;
  allowDelete?: boolean;
  disabled?: boolean;
  sourceQuestionId?: string;
  sourceTableData?: any[];
}

function EventsTable({
  columns,
  value,
  onChange,
  allowAdd = true,
  allowDelete = true,
  disabled = false,
  sourceQuestionId,
  sourceTableData,
}: EventsTableProps) {
  const rows = Array.isArray(value) ? value : [];
  
  // If this table has a source table, sync rows when source data changes
  React.useEffect(() => {
    if (sourceQuestionId && sourceTableData && Array.isArray(sourceTableData)) {
      // Find columns that need to be populated from source
      const sourceColumns = columns.filter(col => col.readOnly && col.sourceColumn);
      
      if (sourceColumns.length > 0) {
        // Create rows based on source table data
        const newRows = sourceTableData.map((sourceRow: any) => {
          const newRow: any = {};
          columns.forEach((col) => {
            if (col.readOnly && col.sourceColumn) {
              // Populate from source table
              newRow[col.key] = sourceRow[col.sourceColumn] || "";
            } else {
              // Keep existing value or initialize empty
              const existingRow = rows.find((r) => {
                // Try to match by source columns
                return sourceColumns.every(sc => {
                  const sourceVal = sourceRow[sc.sourceColumn!];
                  return r[sc.key] === sourceVal;
                });
              });
              newRow[col.key] = existingRow?.[col.key] || "";
            }
          });
          return newRow;
        });
        
        // Only update if the number of rows changed or structure is different
        if (newRows.length !== rows.length || 
            newRows.some((newRow: any, idx: number) => {
              const oldRow = rows[idx];
              return !oldRow || sourceColumns.some(sc => newRow[sc.key] !== oldRow[sc.key]);
            })) {
          onChange(newRows);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceTableData, sourceQuestionId]);

  const addRow = () => {
    if (disabled) {
      console.warn("Table is disabled, cannot add row. Please enable edit mode.");
      return;
    }
    // If table has source data, don't allow manual row addition
    if (sourceQuestionId && sourceTableData) {
      console.warn("Table is populated from source table, cannot manually add rows.");
      return;
    }
    const newRow: any = {};
    columns.forEach((col) => {
      newRow[col.key] = col.type === "yes_no" ? "" : "";
    });
    const updatedRows = [...rows, newRow];
    console.log("Adding new row. Current rows:", rows.length, "New rows:", updatedRows.length);
    onChange(updatedRows);
  };

  const updateRow = (index: number, field: string, newValue: string) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: newValue };
    onChange(updated);
  };

  const removeRow = (index: number) => {
    const updated = rows.filter((_, i) => i !== index);
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-white/10 bg-black/20 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-white/5">
              {columns.map((col) => (
                <TableHead key={col.key} className="text-white/80 font-medium">
                  {col.label}
                </TableHead>
              ))}
              {allowDelete && (
                <TableHead className="w-[100px] text-white/80">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (allowDelete ? 1 : 0)}
                  className="py-8 text-center text-white/60"
                >
                  No rows added. Click "Add Row" to add a new entry.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={index} className="border-white/10 hover:bg-white/5">
                  {columns.map((col) => (
                    <TableCell key={col.key}>
                      {col.readOnly ? (
                        <div className="text-white/90 py-2 px-3 bg-black/30 border border-white/10 rounded">
                          {row[col.key] || "-"}
                        </div>
                      ) : col.type === "yes_no" ? (
                        <Select
                          value={row[col.key] || ""}
                          onValueChange={(val) => updateRow(index, col.key, val)}
                          disabled={disabled}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`Select ${col.label.toLowerCase()}`} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Yes">Yes</SelectItem>
                            <SelectItem value="No">No</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : col.type === "dropdown" && col.options ? (
                        <Select
                          value={row[col.key] || ""}
                          onValueChange={(val) => updateRow(index, col.key, val)}
                          disabled={disabled}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={`Select ${col.label.toLowerCase()}`} />
                          </SelectTrigger>
                          <SelectContent>
                            {col.options.map((option) => (
                              <SelectItem key={option} value={option}>
                                {option}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : col.type === "textarea" ? (
                        <Textarea
                          value={row[col.key] || ""}
                          onChange={(e) => updateRow(index, col.key, e.target.value)}
                          disabled={disabled}
                          className="text-white border-white/10 bg-black/20 min-h-[80px]"
                          placeholder={`Enter ${col.label.toLowerCase()}`}
                        />
                      ) : (
                        <Input
                          value={row[col.key] || ""}
                          onChange={(e) => updateRow(index, col.key, e.target.value)}
                          disabled={disabled}
                          className="text-white border-white/10 bg-black/20"
                          placeholder={`Enter ${col.label.toLowerCase()}`}
                        />
                      )}
                    </TableCell>
                  ))}
                  {allowDelete && (
                    <TableCell>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => removeRow(index)}
                        disabled={disabled}
                        className="text-white/80 hover:text-white border-white/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {allowAdd && !(sourceQuestionId && sourceTableData) && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addRow();
          }}
          disabled={disabled}
          className="text-white/80 hover:text-white border-white/10"
        >
          Add Row
        </Button>
      )}
    </div>
  );
}

